/**
 * Bounded LRU cache for blob: cover-art object URLs.
 * Every createObjectURL entry must be released via revokeObjectURL on eviction.
 */

/** Desktop default — scaled down on low-RAM devices via navigator.deviceMemory. */
export const COVER_CACHE_DESKTOP_MAX_ENTRIES = 3000;
export const COVER_CACHE_DESKTOP_MAX_ESTIMATED_BYTES = 400 * 1024 * 1024;

function resolveCoverCacheLimits(): { maxEntries: number; maxBytes: number } {
  const mem = typeof navigator !== 'undefined'
    ? (navigator as Navigator & { deviceMemory?: number }).deviceMemory
    : undefined;
  if (typeof mem === 'number' && Number.isFinite(mem)) {
    if (mem <= 4) return { maxEntries: 800, maxBytes: 120 * 1024 * 1024 };
    if (mem <= 8) return { maxEntries: 1500, maxBytes: 250 * 1024 * 1024 };
  }
  return {
    maxEntries: COVER_CACHE_DESKTOP_MAX_ENTRIES,
    maxBytes: COVER_CACHE_DESKTOP_MAX_ESTIMATED_BYTES,
  };
}

const _coverCacheLimits = resolveCoverCacheLimits();
export const COVER_CACHE_MAX_ENTRIES = _coverCacheLimits.maxEntries;
export const COVER_CACHE_MAX_ESTIMATED_BYTES = _coverCacheLimits.maxBytes;

/** Default decoded thumb estimate when blob.size is unavailable. */
const DEFAULT_ENTRY_BYTES = 80 * 1024;

type CacheEntry = {
  url: string;
  estimatedBytes: number;
};

const lru = new Map<string, CacheEntry>();
let estimatedTotalBytes = 0;

function entryBytes(blob: Blob | null | undefined): number {
  if (blob && typeof blob.size === 'number' && blob.size > 0) return blob.size;
  return DEFAULT_ENTRY_BYTES;
}

function touch(key: string, entry: CacheEntry): void {
  lru.delete(key);
  lru.set(key, entry);
}

function revokeEntry(entry: CacheEntry): void {
  try {
    URL.revokeObjectURL(entry.url);
  } catch {
    /* best effort */
  }
}

function evictOne(): void {
  const oldest = lru.keys().next().value as string | undefined;
  if (!oldest) return;
  const entry = lru.get(oldest);
  if (entry) {
    revokeEntry(entry);
    estimatedTotalBytes = Math.max(0, estimatedTotalBytes - entry.estimatedBytes);
  }
  lru.delete(oldest);
}

function enforceBounds(): void {
  while (lru.size > COVER_CACHE_MAX_ENTRIES) {
    evictOne();
  }
  while (estimatedTotalBytes > COVER_CACHE_MAX_ESTIMATED_BYTES && lru.size > 0) {
    evictOne();
  }
}

/** Store or return an existing blob URL for `key`. */
export function putBlobCoverUrl(
  key: string,
  blob: Blob,
): string {
  const existing = lru.get(key);
  if (existing) {
    touch(key, existing);
    return existing.url;
  }
  const url = URL.createObjectURL(blob);
  const entry: CacheEntry = { url, estimatedBytes: entryBytes(blob) };
  lru.set(key, entry);
  estimatedTotalBytes += entry.estimatedBytes;
  enforceBounds();
  return url;
}

export function getBlobCoverUrl(key: string): string | null {
  const entry = lru.get(key);
  if (!entry) return null;
  touch(key, entry);
  return entry.url;
}

export function hasBlobCoverUrl(key: string): boolean {
  return lru.has(key);
}

export function removeBlobCoverUrl(key: string): void {
  const entry = lru.get(key);
  if (!entry) return;
  revokeEntry(entry);
  estimatedTotalBytes = Math.max(0, estimatedTotalBytes - entry.estimatedBytes);
  lru.delete(key);
}

export function clearBlobCoverCache(): void {
  for (const entry of lru.values()) {
    revokeEntry(entry);
  }
  lru.clear();
  estimatedTotalBytes = 0;
}

export function getBlobCoverCacheStats(): {
  entryCount: number;
  estimatedBytes: number;
  outstandingBlobUrls: number;
} {
  return {
    entryCount: lru.size,
    estimatedBytes: estimatedTotalBytes,
    outstandingBlobUrls: lru.size,
  };
}

let pagehideListenerInstalled = false;

/** Register pagehide cleanup — call once at app startup (not at module load). */
export function installBlobCoverCacheLifecycle(): void {
  if (pagehideListenerInstalled || typeof window === 'undefined') return;
  if (typeof window.addEventListener !== 'function') return;
  pagehideListenerInstalled = true;
  window.addEventListener('pagehide', () => clearBlobCoverCache());
}
